import {
  type InfiniteScrollResponse,
  useInfiniteScroll,
  useLazyGetTourSearchLinksResultsQuery,
} from '@carboncopyco/data';
import type {
  ShareLink as NoneIndexedShareLink,
  NotesInDesktopMessageProps,
  Tour,
  Workspace,
  WorkspaceFolder,
} from '@carboncopyco/types';
import { useBoolean } from 'ahooks';
import constate from 'constate';
import { useState } from 'react';

type ShareLink = {
  [key: string]: unknown;
} & NoneIndexedShareLink;

export type TourPageProps = {
  rootPath: string;
  goBack: (workspaceId: Workspace['id']) => void;
  organizationId: string;
  tourId: Tour['id'];
  workspaceId: Workspace['id'];
  handleEdit: () => void;
  goToTourPreview: (tourId: Tour['id']) => void;
  goToCopiedTour: (tourId: Tour['id'], workspaceId: Workspace['id']) => void;
  tourDemoPath: string;
  goToLinksTab: () => void;
  goToTour: (tour: Tour) => void;
  toursShareAspectRatioEnabled?: boolean;
  goToWorkspacePage: (workspaceId: Workspace['id']) => void;
  goToFolderPage?: (
    folderId: WorkspaceFolder['id'],
    workspaceId: Workspace['id']
  ) => void;
  goToTourPresentation?: (token: string) => void;
  readOnly?: boolean;
};

export type TourPageState = TourPageProps &
  NotesInDesktopMessageProps & {
    noteValue: string;
    setNoteValue: (value: string) => void;
    pendingNavigationRoute: string;
    setPendingNavigationRoute: (route: string) => void;
    tourLinksResponse: InfiniteScrollResponse<ShareLink>;
  };

const useTourPage = (props: TourPageProps): TourPageState => {
  const [noteValue, setNoteValue] = useState('');
  const [pendingNavigationRoute, setPendingNavigationRoute] =
    useState<string>();
  const [showNotesInDesktopMessage, setShowNotesInDesktopMessage] =
    useBoolean(true);

  const tourLinksResponse = useInfiniteScroll(
    useLazyGetTourSearchLinksResultsQuery
  ) as InfiniteScrollResponse<ShareLink>;

  return {
    ...props,
    noteValue,
    setNoteValue,
    showNotesInDesktopMessage,
    pendingNavigationRoute,
    setPendingNavigationRoute,
    onCloseNotesInDesktopMessage: setShowNotesInDesktopMessage.setFalse,
    tourLinksResponse,
  };
};

export const [TourPageProvider, useTourPageProps, useTourShares] = constate(
  useTourPage,
  (value) => value,
  ({ tourLinksResponse }) => tourLinksResponse
);
